/**
 * The super-admin feature hubs, and the content pages each one owns.
 *
 * ROUTE CONVENTION
 * ----------------
 * A feature lives at the root — `/meditation`, `/sleep`, `/breathwork` — the
 * same way `/pulse-survey` already does. Everything about that feature hangs
 * off it:
 *
 *   /meditation                 the dashboard, with the audience selector
 *   /meditation/b2b             companies, drilling into one company
 *   /meditation/b2c             direct users, drilling into one person
 *   /meditation/manage          the content library (was
 *                               /content-management/meditation)
 *
 * The old `/content-management/<feature>` tree is gone rather than redirected:
 * a redirect leaves the old path as the one people bookmark and the one that
 * shows up in the address bar for a moment on every internal navigation. The
 * ~240 internal links were rewritten in the same pass.
 *
 * Features with no analytics hub still moved to the root — `/affirmation`,
 * `/focus`, `/gratitude` — because the convention is about the panel, not
 * about which features happen to have dashboards.
 *
 * WHY A REGISTRY
 * --------------
 * Nineteen hubs with the same three tabs and the same shell. Holding them as
 * data means the tab row, the manage dropdown and the route table are all
 * generated from one description, so a hub cannot end up with tabs that
 * disagree with its routes.
 */

/**
 * How much of the standard hub a feature actually has.
 *
 *   'full'     dashboard + companies + B2C users. The default.
 *   'noB2C'    dashboard + companies. A feature only companies have, so there
 *              is no direct-user population to show.
 *   'company'  companies only, opening straight into one. For features whose
 *              figures are meaningless without a company — Perks reports the
 *              scheme catalogue a company bought, Challenges the challenges it
 *              created, and neither can be derived from an employee list.
 */
export const HUB_SHAPE = {
  FULL: 'full',
  NO_B2C: 'noB2C',
  COMPANY: 'company'
};

/**
 * A hub's tab row.
 *
 * Three tabs by default — dashboard, companies, users — because that is the
 * shape agreed for every feature hub. A hub that genuinely has two dashboards
 * (Sleep reports the sound library and the sleep tracker, which are different
 * questions about different data) declares `tabs` itself and this steps aside.
 */
export const hubTabs = (hub) => {
  if (hub.tabs) return hub.tabs;

  if (hub.shape === HUB_SHAPE.COMPANY) {
    // One tab is not a tab row; the section is just the company list.
    return [];
  }

  return [
    { label: 'Dashboard', path: hub.basePath },
    { label: 'B2B Companies', path: `${hub.basePath}/b2b` },
    ...(hub.shape === HUB_SHAPE.NO_B2C ? [] : [{ label: 'B2C Users', path: `${hub.basePath}/b2c` }])
  ];
};

/**
 * Content pages for a feature, as the "Manage content" dropdown's options.
 *
 * A dropdown rather than a fourth tab: managing the library is a different job
 * from reading the analytics, and a feature can own several content pages
 * (the list, the editor, sometimes a separate category page) which would blow
 * the tab row out. Empty means the feature has no content to manage — Sleep's
 * tracker, for instance, records what people did rather than anything Shoorah
 * publishes.
 */
export const ADMIN_HUBS = {
  meditation: {
    key: 'meditation',
    title: 'Meditation',
    description:
      'The meditation library, who is listening to it, and how companies and direct users compare.',
    basePath: '/meditation',
    manage: [
      { label: 'All meditations', path: '/meditation/manage' },
      { label: 'Add meditation', path: '/meditation/manage/add-edit' }
    ]
  },

  sleep: {
    key: 'sleep',
    title: 'Sleep',
    description:
      'The sleep sound library, sleep-tracking activity, and how companies and direct users are sleeping.',
    basePath: '/sleep',
    tabs: [
      { label: 'Sounds Dashboard', path: '/sleep' },
      { label: 'Tracker Dashboard', path: '/sleep/tracker' },
      { label: 'B2B Companies', path: '/sleep/b2b' },
      { label: 'B2C Users', path: '/sleep/b2c' }
    ],
    manage: [
      { label: 'All sounds', path: '/sleep/manage' },
      { label: 'Add sound', path: '/sleep/manage/add-edit' }
    ]
  },

  breathwork: {
    key: 'breathwork',
    title: 'Breathwork',
    description:
      'The breathwork library, session activity, and how companies and direct users are breathing.',
    basePath: '/breathwork',
    tabs: [
      { label: 'Sessions Dashboard', path: '/breathwork' },
      { label: 'Audio Dashboard', path: '/breathwork/audio' },
      { label: 'B2B Companies', path: '/breathwork/b2b' },
      { label: 'B2C Users', path: '/breathwork/b2c' }
    ],
    manage: [
      { label: 'All breathwork', path: '/breathwork/manage' },
      { label: 'Add breathwork', path: '/breathwork/manage/add-edit' }
    ]
  },

  habitTracker: {
    key: 'habit_tracker',
    title: 'Habit Tracker',
    description: 'The habit library, and how companies and direct users are building habits.',
    basePath: '/habit-tracker',
    manage: [
      { label: 'All habits', path: '/habit-tracker/manage' },
      { label: 'Add habit', path: '/habit-tracker/manage/add-edit' }
    ]
  },

  shoorahVids: {
    key: 'shoorah_vids',
    title: 'Shoorah Vids',
    description: 'The video library, views, and how companies and direct users are watching.',
    basePath: '/shoorah-vids',
    // The videos are managed under Guidance, which is its own root section.
    manage: [{ label: 'Manage videos', path: '/guidance' }]
  },

  visionBoard: {
    key: 'vision_board',
    title: 'Vision Board',
    description:
      'The vision board idea library, and what companies and direct users are putting on their boards.',
    basePath: '/vision-board',
    manage: [
      { label: 'All ideas', path: '/vision-board/manage' },
      { label: 'Add idea', path: '/vision-board/manage/add-edit' }
    ]
  },

  selfAssessment: {
    key: 'self_assessment',
    title: 'Self-Assessment',
    description:
      'The assessment catalogue, who is taking them, and what companies and direct users score.',
    // The bare path, like every other hub. The assessment BUILDER used to own
    // it and now sits under /self-assessment/manage with the other content
    // pages — authoring the questionnaires is a different job from reporting
    // on the answers.
    basePath: '/self-assessment',
    manage: [
      { label: 'Assessment overview', path: '/self-assessment/manage/overview' },
      { label: 'All assessments', path: '/self-assessment/manage' },
      { label: 'Create assessment', path: '/self-assessment/manage/create' }
    ]
  },

  shuru: {
    key: 'shuru',
    title: 'Shuru',
    description: 'Conversations with Shuru, and how companies and direct users are using it.',
    basePath: '/shuru',
    // Shuru is a conversation, not a catalogue — nothing to manage.
    manage: []
  },

  mood: {
    key: 'mood_tracking',
    title: 'Mood Tracking',
    description:
      'Mood check-ins, what people are feeling, and how companies and direct users compare.',
    basePath: '/mood',
    // The emotion taxonomy is managed under Config, not per-feature.
    manage: []
  },

  homeTesting: {
    key: 'home_testing',
    title: 'Home Testing',
    description:
      'Vitall home test kits — volume and where each kit is in its journey. What a test found is never reported here.',
    basePath: '/home-testing',
    manage: []
  },

  eliteMind: {
    key: 'elite_mind',
    title: 'Elite Mind',
    description:
      'Athlete journals, the reports athletes chose to share, and the squads behind them.',
    basePath: '/elite-mind',
    manage: []
  },

  moneyMind: {
    key: 'moneymind',
    title: 'MoneyMind',
    description: 'Money-map entries and the practice behind them. Amounts are never reported.',
    basePath: '/moneymind',
    manage: []
  },

  scans: {
    key: 'scans',
    title: 'Scans',
    description: 'Skin, teeth and age scans — volume and journey, never a finding.',
    basePath: '/scans',
    manage: []
  },

  games: {
    key: 'game_tracking',
    title: 'Games',
    description: 'Chess, word search, block fill and quiz — who is playing, and how much.',
    basePath: '/games',
    // No per-player page: the company panel has none either, because a game
    // score says nothing useful about one person.
    hasUserDetail: false,
    manage: []
  },

  guidedPath: {
    key: 'guided_path',
    title: 'Guided Path',
    description: 'AI and custom plans — who is on one, and how far through.',
    basePath: '/guided-path',
    hasUserDetail: false,
    manage: []
  },

  triggerTrace: {
    key: 'trigger_trace',
    title: 'Trigger Trace',
    description: 'What sets people off, what they do about it, and where in life it comes from.',
    basePath: '/trigger-trace',
    hasUserDetail: false,
    manage: []
  },

  selfDevelopment: {
    key: 'self_development',
    title: 'Self Development',
    description:
      'Gratitude, cleanse, journal, goals, affirmations and vision boards — the tools people reach for on their own.',
    basePath: '/self-development',
    manage: []
  },

  cessation: {
    key: 'cessation',
    title: 'Cessation',
    description: 'Last Puff and Last Drink — programmes, not people.',
    basePath: '/cessation',
    // No per-person page: a table of who is trying to quit what is exactly
    // what this feature reports around rather than through.
    hasUserDetail: false,
    hasUsersList: false,
    shape: HUB_SHAPE.NO_B2C,
    manage: []
  },

  marketplace: {
    key: 'marketplace',
    title: 'Marketplace',
    description: 'E-gift redemptions across every company on a plan.',
    basePath: '/marketplace-insights',
    shape: HUB_SHAPE.NO_B2C,
    tabs: [
      { label: 'E-Gifts', path: '/marketplace-insights' },
      { label: 'B2B Companies', path: '/marketplace-insights/b2b' }
    ],
    // VIP offer analytics live in Marketplace → VIP Offers → Dashboard.
    manage: [
      { label: 'VIP offers dashboard', path: '/marketplace?type=VIP&view=dashboard' },
      { label: 'Manage offers', path: '/marketplace' }
    ]
  },

  perks: {
    key: 'perks',
    title: 'Perks',
    description:
      'Instant support, salary sacrifice, health schemes and custom perks — open a company to see what it runs.',
    basePath: '/perks',
    shape: HUB_SHAPE.COMPANY,
    manage: [{ label: 'Manage offerings', path: '/offering' }]
  },

  challenges: {
    key: 'challenges',
    title: 'Challenges',
    description: 'Company challenges and who is taking part. Open a company to see its challenges.',
    basePath: '/challenges-insights',
    shape: HUB_SHAPE.COMPANY,
    manage: [{ label: 'Manage challenges', path: '/challenges' }]
  },

  shoorahPods: {
    key: 'shoorah_pods',
    title: 'Shoorah Pods',
    description: 'The pod library, plays, and how companies and direct users are listening.',
    basePath: '/shoorah-pods',
    manage: [
      { label: 'All pods', path: '/shoorah-pods/manage' },
      { label: 'Add pod', path: '/shoorah-pods/manage/add-edit' }
    ]
  }
};

export const getHub = (key) => ADMIN_HUBS[key] || null;
